import React, { useEffect, useRef, useState } from "react";
import {
  MoreHorizontal,
  Printer,
  FileSpreadsheet,
  RefreshCw,
  Layers,
  Repeat,
  History,
  AlertTriangle,
} from "lucide-react";
import { usePerms } from "@/lib/auth";

/**
 * MoreActionsMenu — "…" dropdown for the Orders toolbar. Groups low-frequency
 * admin actions (backfills, reclassify, wipe, SKU refill) plus a couple of
 * everyday ops helpers (print labels, template download) so the toolbar can
 * stay in a single tidy row. Feb 28 2026.
 */
export default function MoreActionsMenu({
  isAdmin,
  bulkPrintLabels,
  downloadTemplate,
  onBackfill,
  onAllocatedShipped,
  onReclassify,
  onFetchSku,
  onWipe,
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const fire = (fn) => () => {
    setOpen(false);
    if (typeof fn === "function") fn();
  };

  const Item = ({ icon: Icon, label, hint, onClick, danger, testId }) => (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`w-full text-left px-3 py-2 text-[12.5px] inline-flex items-start gap-2 border-b border-slate-50 last:border-0 ${
        danger ? "hover:bg-red-50 text-red-700" : "hover:bg-slate-50 text-slate-700"
      }`}
    >
      <Icon size={13} className="mt-[2px] shrink-0" />
      <span className="flex-1">
        <span className="block font-semibold">{label}</span>
        {hint && <span className="block text-[11px] text-slate-500 leading-snug">{hint}</span>}
      </span>
    </button>
  );

  const { canFeature, canFeatureExplicit } = usePerms();
  // Mar 1 2026 — feature-permission gates. `canFeature` returns true for
  // admins and for any user without an explicit `feature_permissions[name]
  // === false`. If a button is disabled, we drop it (not grey it out —
  // fully hidden per the spec).
  const showPrint = canFeature("print_labels");
  const showTemplate = canFeature("download_template");
  // Aug 31 2026 — was `isAdmin && canFeature(...)`, which made the per-user
  // "Admin Backfills" toggle in the Users admin panel dead: it could never
  // actually grant a non-admin the button since isAdmin was already false.
  // Use the strict/opt-in check instead so a user explicitly granted the
  // toggle sees the button; admins still bypass via canFeatureExplicit.
  const showBackfills = canFeatureExplicit("admin_backfills");
  // When every operations button is hidden AND admin backfills are off,
  // the whole menu is empty — bail early so the "…" trigger disappears too.
  if (!showPrint && !showTemplate && !showBackfills) return null;

  return (
    <div className="relative" ref={rootRef} data-testid="orders-more-menu">
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        data-testid="orders-more-btn"
        title="More actions"
        className="h-8 w-8 inline-flex items-center justify-center border border-slate-200 rounded-sm bg-white text-slate-600 hover:bg-slate-100 hover:text-slate-900"
      >
        <MoreHorizontal size={14} />
      </button>
      {open && (
        <div
          className="absolute top-full right-0 mt-1 z-40 bg-white border border-slate-200 rounded-sm shadow-lg w-[280px] overflow-hidden"
          data-testid="orders-more-menu-panel"
        >
          {(showPrint || showTemplate) && (
            <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-slate-500 bg-slate-50 border-b border-slate-100">
              Operations
            </div>
          )}
          {showPrint && (
            <Item
              icon={Printer}
              label="Print Labels (selected)"
              hint="Batch-print packing slips for the highlighted orders."
              onClick={fire(bulkPrintLabels)}
              testId="orders-more-print"
            />
          )}
          {showTemplate && (
            <Item
              icon={FileSpreadsheet}
              label="Download Template"
              hint="Empty CSV with every field so ops can paste-in bulk."
              onClick={fire(downloadTemplate)}
              testId="orders-more-template"
            />
          )}

          {showBackfills && (
            <>
              <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-slate-500 bg-slate-50 border-t border-b border-slate-100">
                Admin · Backfills
              </div>
              <Item
                icon={RefreshCw}
                label="Backfill Marketplace Status"
                hint="Re-derive MP status for legacy rows (dry-run first)."
                onClick={fire(onBackfill)}
                testId="orders-more-backfill"
              />
              <Item
                icon={Layers}
                label="Allocated → Shipped"
                hint="Flip already-allocated Seller-Flex, Easy-Ship and Flipkart orders to Shipped (Self-Ship untouched)."
                onClick={fire(onAllocatedShipped)}
                testId="orders-more-allocated-shipped"
              />
              <Item
                icon={Repeat}
                label="Reclassify China Status"
                hint="Rewrite china_order_status per the current rulebook."
                onClick={fire(onReclassify)}
                testId="orders-more-reclassify"
              />
              <Item
                icon={History}
                label="Fetch System SKU from Mapping"
                hint="Refill blank system_sku values using the mappings table."
                onClick={fire(onFetchSku)}
                testId="orders-more-fetch-sku"
              />
              <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-red-600 bg-red-50/50 border-t border-b border-red-100">
                Danger zone
              </div>
              <Item
                icon={AlertTriangle}
                label="Wipe Orders"
                hint="Destructive — deletes every order and reverses allocations."
                onClick={fire(onWipe)}
                danger
                testId="orders-more-wipe"
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}
